import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions
} from "react-native";

import { Artwork, Backdrop } from "@/components/Artwork";
import { useCardBackdropHandoff } from "@/components/PageBackdrop";
import { SpecularEdge } from "@/components/SpecularEdge";
import { ChevronRightIcon } from "@/design/icons";
import { webControlHoverTransition } from "@/design/interactionMotion";
import { Muted, Overline, Text } from "@/design/Text";
import {
  contentMaxWidth,
  focusRing,
  gradients,
  hoverRing,
  palette,
  RECITAL_COVER_ASPECT_RATIO,
  radius,
  space
} from "@/design/tokens";

/**
 * Art-forward row card: poster left, text middle, and an action mini-card
 * (the "up next" treatment) filling the right side. The card body opens the
 * item; the action card is a playback shortcut. They are sibling pressables
 * (never nested buttons). Hover/focus states are flat — border + wash, no
 * lift — so they translate to keyboard and TV focus.
 */

type MediaCardAction = {
  /** May carry inline status, e.g. "Resume watching · 0:09 left". */
  overline: string;
  label: string;
  onPress: () => void;
};

export type MediaCardLayout = "compact" | "split" | "wide";

type MediaCardBaseProps = {
  title: string;
  /** Small line above the title, e.g. "2026 Recital". */
  kicker?: string | null;
  metaLines: string[];
  /**
   * Split tier only: meta as one full-width justified row — leading pinned
   * left, trailing pinned right — echoing the fused footer's structure so
   * short text never leaves a dead zone beside the fan. Wraps back to
   * stacked lines when the text column is too narrow. Stacked and wide
   * tiers always use metaLines.
   */
  metaRow?: { leading: string; trailing: string };
  artworkUri: string | null;
  /**
   * Override the card's internal glow image. By default the card glows with
   * its own blurred artwork — right for pages whose backdrop is that same
   * artwork. Brand-backed pages (home) pass the page's brand image instead,
   * so card and page share one palette and the artwork pops in the poster.
   */
  backdropUri?: string;
  /**
   * Let the persistent page backdrop show through while this card resolves
   * into its own background. Reserved for index cards whose destination page
   * is the atmosphere being handed back.
   */
  inheritPageBackdrop?: boolean;
  /**
   * Opt into chroma restoration on this card's glow — see `Backdrop`'s
   * `saturateArtwork`. For cards glowing with a stage still, which the scrim
   * flattens to brown. Never for the brand-art cards on home.
   */
  saturateBackdrop?: boolean;
  onPress: () => void;
  accessibilityLabel: string;
  action?: MediaCardAction | null;
  badge?: ReactNode;
  /** RecitalHero's progress grammar — gradient sliver + label, one voice
      everywhere a recital shows its face. */
  progress?: { fraction: number; label: string } | null;
};

type MediaCardProps = MediaCardBaseProps & (
  | {
      posterSlot?: undefined;
      artworkSeed: string;
      artworkLabel?: string;
    }
  | {
      posterSlot: (state: {
        centered: boolean;
        lit: boolean;
        layout: MediaCardLayout;
      }) => ReactNode;
      artworkSeed?: string;
      artworkLabel?: string;
    }
);

/** Mouse clicks focus web pressables too — only keyboard focus earns a ring. */
function isKeyboardFocus(event: { nativeEvent?: unknown }): boolean {
  // Native phone Pressables can retain focus after an ordinary touch or route
  // return. Treating every native focus as keyboard/TV intent leaves one card
  // permanently cool-washed and makes identical backdrops look different.
  // This app targets handheld native shells; web's :focus-visible remains the
  // authoritative keyboard-focus signal.
  if (Platform.OS !== "web") return false;
  const target = (event?.nativeEvent as { target?: unknown } | undefined)?.target;
  return typeof Element !== "undefined" && target instanceof Element
    ? target.matches(":focus-visible")
    : false;
}

// The card-level ring (and the action's own focusRing) carry keyboard focus;
// the browser's default outline on the inner pressable would otherwise draw
// a stray rectangle whose edge cuts through the middle of the card.
const noInnerOutline = Platform.select({
  web: { outlineStyle: "none" } as unknown as object,
  default: undefined
});

// `coolWashFaint` is a 5% tint. Partial opacity keeps the effective hover
// lift near 2% across both saturated brand art and darker recital imagery.
const HOVER_LIGHT_OPACITY = 0.45;

// The justified metadata row stops fitting beside the poster at this card
// width and its trailing value stacks underneath. That collapsed composition
// spans both the narrow end of the split tier and the compact phone tier, so
// centering follows the measured card width rather than the broader tier name.
const COLLAPSED_POSTER_GROUP_BREAKPOINT = 520;

export function MediaCard({
  title,
  kicker,
  metaLines,
  metaRow,
  artworkUri,
  backdropUri,
  inheritPageBackdrop = false,
  saturateBackdrop = false,
  artworkSeed,
  artworkLabel,
  posterSlot,
  onPress,
  accessibilityLabel,
  action,
  badge,
  progress
}: MediaCardProps) {
  const viewport = useWindowDimensions();
  const progressWidth =
    `${Math.min(1, Math.max(0, progress?.fraction ?? 0)) * 100}%` as `${number}%`;
  const [lit, setLit] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [actionFocusVisible, setActionFocusVisible] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const hoverLight = useRef(new Animated.Value(0)).current;
  // Press feedback is a scrim over the WHOLE card, not content opacity — the
  // body's poster/text sit on the card's own lit backdrop, so dimming them
  // toward it is imperceptible. The card must press as one object.
  const pressScrim = useRef(new Animated.Value(0)).current;
  const scrimPressIn = () => {
    pressScrim.stopAnimation();
    pressScrim.setValue(1);
  };
  const scrimPressOut = () => {
    Animated.timing(pressScrim, {
      toValue: 0,
      duration: 180,
      easing: Easing.out(Easing.quad),
      useNativeDriver: Platform.OS !== "web"
    }).start();
  };
  const cardBackdropHandoff = useCardBackdropHandoff();
  const backdropReveal = Animated.subtract(1, cardBackdropHandoff.opacity);
  const inheritsActiveHandoff =
    inheritPageBackdrop && cardBackdropHandoff.uri != null;
  const estimatedCardWidth = Math.max(
    0,
    Math.min(viewport.width, contentMaxWidth(viewport.width)) -
      2 * (Platform.OS === "web" ? space.xl : space.lg)
  );
  const resolvedCardWidth = cardWidth || estimatedCardWidth;
  // Choose the tier from the card's own measured width, not the window —
  // cards live in columns of varying width. Before the first layout event,
  // Screen's known content geometry prevents a desktop card from flashing on
  // phones during the native route transition.
  const layout: MediaCardLayout = resolvedCardWidth < 360
    ? "compact"
    : resolvedCardWidth < 720
      ? "split"
      : "wide";
  const fusedAction = layout !== "wide";
  const centersCollapsedPosterGroup =
    Boolean(posterSlot) &&
    resolvedCardWidth < COLLAPSED_POSTER_GROUP_BREAKPOINT;
  const usesWidePosterGroup = Boolean(posterSlot) && layout === "wide";

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setLit(true),
      onMouseLeave: () => setLit(false)
    } as object,
    default: {}
  });

  useEffect(() => {
    const reduceMotion =
      Platform.OS === "web" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    hoverLight.stopAnimation();
    if (reduceMotion) {
      hoverLight.setValue(lit ? HOVER_LIGHT_OPACITY : 0);
      return undefined;
    }

    const animation = Animated.timing(hoverLight, {
      toValue: lit ? HOVER_LIGHT_OPACITY : 0,
      duration: lit ? 160 : 240,
      easing: lit ? Easing.out(Easing.cubic) : Easing.out(Easing.quad),
      useNativeDriver: Platform.OS !== "web"
    });
    animation.start();
    return () => animation.stop();
  }, [hoverLight, lit]);

  return (
    <View
      onLayout={(event) => {
        setCardWidth(event.nativeEvent.layout.width);
      }}
      {...hoverProps}
      style={[
        styles.card,
        keyboardFocused && Platform.select({ web: focusRing as object })
      ]}
    >
      <Animated.View
        style={[
          styles.cardBackdrop,
          { opacity: inheritsActiveHandoff ? backdropReveal : 1 }
        ]}
      >
        <View style={styles.cardSurface} />
        <Backdrop
          uri={backdropUri ?? artworkUri}
          // The fallback belongs to the image it stands in for. Home cards
          // deliberately share one branded backdrop, so they must also share
          // its fallback instead of inheriting recital-specific poster colors.
          fallbackSeed={backdropUri ?? artworkSeed ?? artworkUri ?? title}
          intensity={0.75}
          saturateArtwork={saturateBackdrop}
        />
      </Animated.View>
      <Animated.View
        style={[styles.hoverLight, { opacity: hoverLight }]}
      />
      <SpecularEdge
        cornerRadius={radius.xl}
        solidColor={keyboardFocused ? focusRing.borderColor : undefined}
      />
      <View style={[styles.inner, fusedAction && styles.innerNarrow]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          onPress={onPress}
          onPressIn={scrimPressIn}
          onPressOut={scrimPressOut}
          onFocus={(event) => {
            const focusVisible = isKeyboardFocus(event);
            if (focusVisible) {
              setLit(true);
              setKeyboardFocused(true);
            }
          }}
          onBlur={() => {
            setLit(false);
            setKeyboardFocused(false);
          }}
          style={[
            styles.body,
            noInnerOutline,
            fusedAction && styles.bodyNarrow,
            usesWidePosterGroup && styles.bodyWidePoster,
            centersCollapsedPosterGroup && styles.bodyCollapsedPoster,
            layout === "compact" && styles.bodyCompact
          ]}
        >
          {posterSlot ? posterSlot({
            centered: centersCollapsedPosterGroup,
            lit,
            layout
          }) : (
            <Artwork
              uri={artworkUri}
              fallbackSeed={artworkSeed}
              label={artworkLabel}
              style={[styles.poster, fusedAction && styles.posterNarrow]}
            />
          )}
          <View
            style={[
              styles.text,
              centersCollapsedPosterGroup && styles.textCollapsedPoster
            ]}
          >
            {kicker ? <Overline>{kicker}</Overline> : null}
            <Text variant={layout === "compact" ? "title" : "display"} style={styles.title}>
              {title}
            </Text>
            {metaRow && layout !== "wide" ? (
              <View
                style={[
                  styles.metaRow,
                  centersCollapsedPosterGroup && styles.metaRowCollapsed
                ]}
              >
                <Muted variant="small">{metaRow.leading}</Muted>
                <Muted variant="small">{metaRow.trailing}</Muted>
              </View>
            ) : (
              metaLines.map((line) => (
                <Muted key={line} variant="small">
                  {line}
                </Muted>
              ))
            )}
            {progress ? (
              <View style={styles.progressLine}>
                <View style={styles.progressTrack}>
                  <LinearGradient
                    colors={gradients.playback as unknown as [string, string, ...string[]]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[styles.progressFill, { width: progressWidth }]}
                  />
                </View>
                <Muted variant="small" numberOfLines={1} style={styles.progressLabel}>
                  {progress.label}
                </Muted>
              </View>
            ) : null}
            {badge ? <View style={styles.badgeRow}>{badge}</View> : null}
          </View>
        </Pressable>

        {action ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={action.label}
            onPress={action.onPress}
            onFocus={(event) => setActionFocusVisible(isKeyboardFocus(event))}
            onBlur={() => setActionFocusVisible(false)}
            style={({ pressed, hovered }) => [
              styles.action,
              noInnerOutline,
              fusedAction && styles.actionNarrow,
              hovered && styles.actionHovered,
              !fusedAction && actionFocusVisible && styles.actionFocused,
              actionFocusVisible &&
                Platform.select({ web: focusRing as object, default: hoverRing as object }),
              pressed && styles.actionPressed
            ]}
          >
            {!fusedAction && !actionFocusVisible ? (
              <SpecularEdge />
            ) : null}
            <View style={styles.actionText}>
              <Overline color={palette.faint}>{action.overline}</Overline>
              <Text variant="bodyStrong" color={palette.textSoft} numberOfLines={1}>
                {action.label}
              </Text>
            </View>
            <View style={styles.actionChevron}>
              <ChevronRightIcon size={18} color={palette.muted} />
            </View>
          </Pressable>
        ) : null}
      </View>
      <Animated.View
        pointerEvents="none"
        style={[styles.pressScrim, { opacity: pressScrim }]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radius.xl,
    overflow: "hidden"
  },
  cardBackdrop: {
    ...StyleSheet.absoluteFillObject,
    pointerEvents: "none"
  },
  cardSurface: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.surface
  },
  hoverLight: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: palette.coolWashFaint,
    pointerEvents: "none"
  },
  inner: {
    alignItems: "center",
    flexDirection: "row"
  },
  innerNarrow: {
    alignItems: "stretch",
    flexDirection: "column"
  },
  /* The body pressable owns the card's padding, so the whole card face —
     padding included — presses. Its trailing padding doubles as the gap to
     the action mini-card. */
  body: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: space.xl,
    minWidth: 0,
    padding: space.xl
  },
  bodyNarrow: {
    padding: space.lg
  },
  bodyWidePoster: {
    gap: space.xl + space.xs,
    paddingLeft: space.xxl
  },
  bodyCompact: {
    gap: space.xl,
    padding: space.lg
  },
  bodyCollapsedPoster: {
    justifyContent: "center"
  },
  pressScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(8, 9, 14, 0.16)"
  },
  poster: {
    alignSelf: "flex-start",
    aspectRatio: RECITAL_COVER_ASPECT_RATIO,
    borderRadius: radius.md,
    width: 124
  },
  posterNarrow: {
    width: 92
  },
  text: {
    flex: 1,
    gap: 3,
    minWidth: 0
  },
  textCollapsedPoster: {
    // `flex: -1` is RN's shrink-to-content form (0 1 auto on web). Using
    // individual flex fields here left the base `flex: 1` shorthand active.
    flex: -1,
    maxWidth: "50%"
  },
  title: {
    marginBottom: 2
  },
  badgeRow: {
    flexDirection: "row",
    gap: space.sm,
    marginTop: space.md
  },
  // Identical metrics to RecitalHero's progress line.
  progressLine: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    marginTop: space.sm
  },
  progressTrack: {
    backgroundColor: "rgba(243, 244, 250, 0.22)",
    borderRadius: radius.pill,
    height: 3,
    overflow: "hidden",
    width: 64
  },
  progressFill: {
    height: 3
  },
  progressLabel: {
    flexShrink: 1
  },
  metaRow: {
    alignItems: "baseline",
    columnGap: space.md,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between"
  },
  metaRowCollapsed: {
    alignItems: "flex-start",
    flexDirection: "column",
    justifyContent: "flex-start"
  },
  action: {
    alignItems: "center",
    backgroundColor: "rgba(13, 14, 20, 0.25)",
    borderRadius: radius.lg,
    borderWidth: 0,
    flexDirection: "row",
    gap: space.md,
    marginBottom: space.xl,
    marginRight: space.xl,
    marginTop: space.xl,
    maxWidth: 300,
    minWidth: 190,
    padding: space.lg + 1,
    width: 300,
    ...webControlHoverTransition
  },
  actionFocused: {
    borderColor: focusRing.borderColor,
    borderWidth: 1,
    padding: space.lg
  },
  actionNarrow: {
    alignSelf: "stretch",
    borderTopColor: palette.coolWashStrong,
    borderBottomLeftRadius: radius.xl,
    borderBottomRightRadius: radius.xl,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderWidth: 0,
    borderTopWidth: 1,
    marginBottom: 0,
    marginHorizontal: 0,
    // RN ignores `undefined` overrides, so neutralize the wide-layout
    // constraints with real values; marginRight beats marginHorizontal.
    marginRight: 0,
    marginTop: 0,
    maxWidth: "100%",
    minWidth: 0,
    padding: space.lg,
    width: "100%"
  },
  actionHovered: {
    backgroundColor: "rgba(13, 14, 20, 0.35)",
  },
  // Deepen the surface rather than dim content — opacity here would make the
  // translucent background LIGHTER (more card glow through), fighting the dim.
  actionPressed: {
    backgroundColor: "rgba(13, 14, 20, 0.5)"
  },
  actionText: {
    flex: 1,
    gap: 3
  },
  actionChevron: {
    flexShrink: 0
  }
});
